import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { useSelector } from "react-redux";
import { api, catchErrorHandler } from "utils/api";
import Alert from "components/ui/Alert";
import { MainLayout } from "layouts";
import { Avatar, NotificationIcon } from "components/common";
import Text from "components/ui/Text";
import { getCommaNumber } from "utils/format";
import StoreHeader from "./StoreHeader";
import IconStoreDommae from "assets/icons/store/IconStoreDommae";

const baseOption = {
  threshold: 0.5,
};

const useIntersect = (onIntersect, option) => {
  const [ref, setRef] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkIntersect = ([entry], observer) => {
    if (entry.isIntersecting) {
      onIntersect(entry, observer);
    }
  };

  useEffect(() => {
    let observer;
    if (ref) {
      observer = new IntersectionObserver(checkIntersect, { ...baseOption });
      observer.observe(ref);
    }
    return () => observer && observer.disconnect();
  }, [ref, checkIntersect]);
  return [ref, setRef];
};

const Store = () => {
  const { userInfo } = useSelector((state) => state.user);
  const HeaderComponents = {
    leftComponent: () => {
      return (
        <Text
          color="#092349"
          fontSize="18px"
          fontWeight="700"
          lineHeight="24px"
        >
          센터 전용몰
        </Text>
      );
    },

    rightComponent: () => (
      <RightWrap>
        <NotificationIcon />
        <Link to="/all">
          <Avatar src={userInfo.imageUrl} size={32} minSize={false} />
        </Link>
      </RightWrap>
    ),
  };

  const [list, setList] = useState([]);
  const [scrollEnd, setScrollEnd] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });

  const [_, setRef] = useIntersect(async (entry, observer) => {
    observer.unobserve(entry.target);
    setScrollEnd(true);
    await fetchStoreBanners();
    observer.observe(entry.target);
  }, {});

  const fetchStoreBanners = async () => {
    await api
      .post("/bshop/banners", {
        criteria: {
          page: pagination.page,
          pageSize: pagination.pageSize,
        },
      })
      .then(({ data }) => {
        if (data.first) setList(data.results);
        else setList((prev) => prev.concat(data.results));

        if (data.last) {
          setScrollEnd(true);
          setPagination({ ...pagination, page: 1 });
        } else {
          setScrollEnd(false);
          setPagination({ ...pagination, page: pagination.page + 1 });
        }
      })
      .catch((error) => {
        if (error?.response) {
          return catchErrorHandler(error, "bshopList", false);
        } else {
          Alert("오류가 발생하였습니다<br/>잠시 후 다시 시도해 주십시오");
        }
      });
  };

  useEffect(() => {
    fetchStoreBanners();
  }, []);

  const handleOpenExternalLink = (url) => {
    if (window.ReactNativeWebView) {
      const data = JSON.stringify({
        type: "openExternalLink",
        link: url,
      });
      window.ReactNativeWebView.postMessage(data);
    } else {
      window.location.href = url;
    }
  };

  return (
    <MainLayout
      withHeader={true}
      withBottomTab={true}
      backgroundColor="white"
      headerProps={{
        withBackButton: false,
        padding: "14px 24px 14px 24px",
        leftComponent: HeaderComponents.leftComponent,
        rightComponent: HeaderComponents.rightComponent,
      }}
    >
      <Wrapper>
        <StoreHeader handleOpenExternalLink={handleOpenExternalLink} />
        <TopContent>
          <CustomIconStoreDommae />
          <TextBox>
            <span>센터에 꼭 필요한 상품을</span>
            <b>가장 저렴하게</b>
          </TextBox>
          <LineBanner>
            <span>구매 시 도매꾹 회원가입이 필요합니다.</span>
          </LineBanner>
        </TopContent>
        <StoreBody>
          {list?.map((v) => {
            return (
              <Item
                key={v.seqBshopBanner}
                onClick={() => handleOpenExternalLink(v.url)}
              >
                <img src={v.imgUrl} alt="img" />
                <Text
                  color="primary"
                  fontSize="14px"
                  fontWeight="500"
                  lineHeight="20px"
                  css={css`
                    margin-bottom: 8px;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    word-break: break-word;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                  `}
                >
                  {v.title}
                </Text>
                <Text
                  color="primary"
                  fontSize="16px"
                  fontWeight="600"
                  lineHeight="20px"
                  css={css`
                    margin-left: 4px;
                  `}
                >
                  {getCommaNumber(v.price)}원
                </Text>
              </Item>
            );
          })}
          {!scrollEnd && <div ref={setRef} />}
        </StoreBody>
      </Wrapper>
    </MainLayout>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
`;

const StoreBody = styled.div`
  width: 100%;
  height: auto;
  padding: 0 24px 24px 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px 20px;
  position: relative;
  top: -48px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 40%;
  img {
    width: 100%;
    aspect-ratio: 1 / 1;
    margin-bottom: 8px;
    border: 1px solid #f2f2f2;
    border-radius: 8px;
    object-fit: fill;
  }
`;

const TopContent = styled.div`
  width: 100%;
  padding: 8px 24px;
  background: linear-gradient(180deg, #ffffff 0%, #d7e7fc 135.4%);
  min-height: 231px;
`;

const CustomIconStoreDommae = styled(IconStoreDommae)`
  margin-bottom: 16px;
`;

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > span {
    all: unset;
    font-size: 23px;
    font-weight: 700;
    color: #38455d;
    line-height: 30px;
  }

  & > b {
    all: unset;
    font-size: 23px;
    font-weight: 700;
    color: #508bff;
    line-height: 30px;
  }
`;

const LineBanner = styled.div`
  width: 100%;
  height: 34px;
  margin-top: 12px;

  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffffcc;
  border-radius: 6px;

  & > span {
    font-size: 12px;
    font-weight: 600;
    color: #949db2;
  }
`;

const RightWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 18px;
`;

export default Store;
